import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
function Gallery({
  photos,
  layout = "masonry",
  zoomEffect = true,
  reduced = false
}) {
  const [openAt, setOpenAt] = useState(null);
  const ratios = useMemo(
    () => photos.map((_, i) => [0.78, 1, 1.25, 0.86, 1.12, 0.72][i % 6]),
    [photos]
  );
  if (photos.length === 0) {
    return <div className="glass rounded-2xl px-6 py-14 text-center">
        <p className="font-display text-xl text-[#f3f4f9]">No photographs yet</p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-[color:var(--muted-foreground)]">
          Add photos from the admin panel and they will appear here — first in the order you choose.
        </p>
      </div>;
  }
  const grid = layout === "grid" ? "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" : layout === "carousel" ? "flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 [scrollbar-width:thin]" : "columns-2 gap-3 sm:columns-3 [&>*]:mb-3";
  return <>
      <div className={`${grid} ${zoomEffect ? "" : "no-zoom"}`}>
        {photos.map((p, i) => <button
    key={p.id}
    type="button"
    onClick={() => setOpenAt(i)}
    aria-label={p.caption ? `Open photo: ${p.caption}` : `Open photo ${i + 1}`}
    className={`photo-tile group block w-full text-left focus-visible:outline-offset-4 ${layout === "carousel" ? "w-[74vw] shrink-0 snap-center sm:w-[42%]" : ""}`}
    style={{ aspectRatio: layout === "carousel" ? "4 / 5" : void 0 }}
  >
            <div
    className="relative"
    style={layout !== "carousel" ? { aspectRatio: String(ratios[i]) } : { height: "100%" }}
  >
              <img
    src={p.url}
    alt={p.caption ?? ""}
    loading="lazy"
    decoding="async"
    className="absolute inset-0 h-full w-full object-cover"
  />
              {
    /* caption gradient — always present for legibility, not only on hover
       (there is no hover on a phone) */
  }
              {p.caption && <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,transparent,rgba(11,12,22,.88))] px-3 pb-3 pt-10">
                  <span className="line-clamp-2 text-[0.78rem] leading-snug text-[#eef0f8]">
                    {p.caption}
                  </span>
                </span>}
            </div>
          </button>)}
      </div>

      {openAt !== null && createPortal(<Lightbox
    photos={photos}
    index={openAt}
    onClose={() => setOpenAt(null)}
    onIndex={setOpenAt}
    reduced={reduced}
  />, document.body)}
    </>;
}
function Lightbox({
  photos,
  index,
  onClose,
  onIndex,
  reduced
}) {
  const closeRef = useRef(null);
  const touchStart = useRef(null);
  const next = useCallback(
    () => onIndex((index + 1) % photos.length),
    [index, photos.length, onIndex]
  );
  const prev = useCallback(
    () => onIndex((index - 1 + photos.length) % photos.length),
    [index, photos.length, onIndex]
  );
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, prev, onClose]);
  const photo = photos[index];
  return <div
    role="dialog"
    aria-modal="true"
    aria-label={photo.caption ?? `Photo ${index + 1} of ${photos.length}`}
    className="fixed inset-0 z-[200] flex flex-col bg-[rgba(8,9,18,0.95)] backdrop-blur-md"
    onTouchStart={(e) => {
      touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }}
    onTouchEnd={(e) => {
      const t = touchStart.current;
      if (!t) return;
      const dx = e.changedTouches[0].clientX - t.x;
      const dy = e.changedTouches[0].clientY - t.y;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        if (dx < 0) next();
        else prev();
      }
      touchStart.current = null;
    }}
  >
      <div className="flex items-center justify-between px-4 py-3" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}>
        <span className="text-[0.7rem] uppercase tracking-[0.24em] text-[color:var(--gold-dim)] tabular-nums">
          {index + 1} / {photos.length}
        </span>
        <button data-testid="close-the-photo"
    ref={closeRef}
    type="button"
    onClick={onClose}
    aria-label="Close the photo"
    className="grid h-11 w-11 place-items-center rounded-full border border-[rgba(233,168,242,.3)] text-[#e9a8f2] transition hover:bg-white/10"
  >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2">
        {photos.length > 1 && <button data-testid="previous-photo"
    type="button"
    onClick={prev}
    aria-label="Previous photo"
    className="absolute left-2 z-10 grid h-12 w-12 place-items-center rounded-full bg-black/45 text-[#e9a8f2] backdrop-blur transition hover:bg-black/70"
  >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
          </button>}

        <img
    key={photo.id}
    src={photo.url}
    alt={photo.caption ?? `Photo ${index + 1} of ${photos.length}`}
    className="max-h-full max-w-full select-none object-contain"
    style={{
      animation: reduced ? void 0 : "lux-fade .45s ease both",
      // Reserve room for the header row AND the caption strip; on a short
      // phone (a 667px iPhone SE) the old fixed 190px left almost nothing
      // for the photo itself.
      maxHeight: "calc(100svh - 220px)"
    }}
  />

        {photos.length > 1 && <button data-testid="next-photo"
    type="button"
    onClick={next}
    aria-label="Next photo"
    className="absolute right-2 z-10 grid h-12 w-12 place-items-center rounded-full bg-black/45 text-[#e9a8f2] backdrop-blur transition hover:bg-black/70"
  >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
          </button>}
      </div>

      <div
    className="min-h-[72px] px-6 pt-2 text-center"
    style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
  >
        {photo.caption && <p className="prose-romance text-sm">{photo.caption}</p>}
      </div>
    </div>;
}
export {
  Gallery
};
